import { isFunction, uniqBy } from "lodash";

import { RadioBigField } from "../../Shared/Fields/RadioBigField";
import { useSyncUrlParam } from "./useSyncUrlParam";
import { useProductsQuery } from "../../hooks/queries/useProductsQuery";

export function VariantSelector({ values, form, render, name = "variant", queryOptions = {} }) {
    const { data: products, isLoading: isProductsLoading } = useProductsQuery({
        queryKey: [name],
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        ...queryOptions,
    });
    useSyncUrlParam(
        "product_number",
        (value) => {
            const product = products.find(
                (product) => product.number === value
            );
            if (product?.productVariants?.length) {
                product.productVariants.forEach((productVariant) =>
                    form.change(
                        `${name}.${productVariant.productVariantOption.key}`,
                        productVariant.value
                    )
                );
            }
        },
        {
            enabled: !isProductsLoading && !!products,
        }
    );

    if (isProductsLoading) {
        return null;
    }

    const productVariantOptions = uniqBy(
        products
            .flatMap((product) => product.productVariants)
            .flatMap((productVariant) => productVariant?.productVariantOption)
            .filter(Boolean)
            .sort((a, b) => a.order - b.order),
        "key"
    );

    return (
        <div className="space-y-6">
            {productVariantOptions?.map((variantOption) => (
                <VariantGroupRenderer
                    queryOptions={queryOptions}
                    form={form}
                    name={name}
                    key={variantOption.key}
                    values={values}
                    variantOption={variantOption}
                    render={render}
                    previousKeys={productVariantOptions
                        .slice(0, productVariantOptions.indexOf(variantOption))
                        .map((option) => option.key)}
                    nextKeys={productVariantOptions
                        .slice(productVariantOptions.indexOf(variantOption) + 1)
                        .map((option) => option.key)}
                />
            ))}
        </div>
    );
}

function VariantGroupRenderer({
    form,
    variantOption,
    values,
    render,
    previousKeys,
    nextKeys,
    name,
    queryOptions = {},
}) {
    const previousKeysAreFilled = previousKeys.every(
        (key) => values?.[name]?.[key]
    );
    const { data: products, isLoading: isProductsLoading } = useProductsQuery({
        ...queryOptions,
        queryKey: [variantOption.key],
        enabled: queryOptions.enabled && previousKeysAreFilled,
        queryParameters: {
            ...(queryOptions.queryParameters ?? {}),
            filter: {
                ...(queryOptions.queryParameters?.filter ?? {}),
                // We want to get all products filtered down by the variants that were already selected
                variant: previousKeys.reduce(
                    (acc, key) => ({
                        ...acc,
                        [key]: values[name]?.[key],
                    }),
                    {}
                ),

            },
        },
    });

    if (isProductsLoading || !products) {
        return null;
    }

    // Find the product variants that belong to this group
    const productVariants = uniqBy(
        products
            .flatMap((product) => product.productVariants)
            .filter(
                (productVariant) =>
                    productVariant.productVariantOption.key ===
                    variantOption.key
            ),
        "value"
    );

    // This group does not have any variants for the selected combinations.
    if (productVariants.length === 0) return null;

    return (
        <div className="space-y-4">
            <h3 className="my-0">{variantOption.question}</h3>
            <div className="grid grid-cols-3 gap-4">
                {productVariants
                    .sort((a, b) => a.value.localeCompare(b.value))
                    .map((productVariant) => (
                        <RadioBigField
                            name={`${name}.${productVariant.productVariantOption.key}`}
                            key={productVariant.id}
                            value={productVariant.value}
                            onChange={() => {
                                nextKeys.forEach((key) =>
                                    form.change(`${name}.${key}`, null)
                                );
                            }}
                        >
                            {isFunction(render)
                                ? render(productVariant)
                                : productVariant.value}
                        </RadioBigField>
                    ))}
            </div>
        </div>
    );
}
